import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { MarkdownEditor } from "../ui/MarkdownEditor";
import { useUpdateRelease } from "../../hooks/useAdminApi";
import type { Release } from "../../types";

interface ReleaseEditDialogProps {
  release: Release | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const toLocalDateTimeInput = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tzOffset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

export const ReleaseEditDialog: React.FC<ReleaseEditDialogProps> = ({
  release,
  open,
  onOpenChange,
}) => {
  const [tagName, setTagName] = useState("");
  const [summary, setSummary] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const update = useUpdateRelease();

  useEffect(() => {
    if (release) {
      setTagName(release.tagName);
      setSummary(release.summary);
      setReleaseDate(toLocalDateTimeInput(release.releaseDate));
    }
  }, [release]);

  const isDirty =
    !!release &&
    (tagName !== release.tagName ||
      summary !== release.summary ||
      releaseDate !== toLocalDateTimeInput(release.releaseDate));

  const handleSave = async () => {
    if (!release) return;
    const payload: { id: string; summary?: string; tagName?: string; releaseDate?: string } = {
      id: release.id,
    };
    if (tagName !== release.tagName) payload.tagName = tagName;
    if (summary !== release.summary) payload.summary = summary;
    if (releaseDate && releaseDate !== toLocalDateTimeInput(release.releaseDate)) {
      payload.releaseDate = new Date(releaseDate).toISOString();
    }
    await update.mutateAsync(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="flex flex-col max-h-[92vh]">
        <DialogHeader>
          <DialogTitle>Editar release</DialogTitle>
          <DialogDescription>
            Ajuste o conteúdo gerado antes de publicar para os clientes.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="release-tagname">Tag</Label>
              <Input
                id="release-tagname"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="release-date">Data de publicação</Label>
              <Input
                id="release-date"
                type="datetime-local"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="release-summary">Conteúdo</Label>
            <MarkdownEditor
              value={summary}
              onChange={setSummary}
              placeholder="Conteúdo em Markdown"
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || update.isLoading}
          >
            {update.isLoading ? "Salvando…" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
